import {
    Component,
    EventEmitter,
    Injector,
    Input,
    NgZone,
    OnInit,
    Output,
} from '@angular/core';
import { NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { IOption } from '@cmps/ng-select';
import { CmpsDialog } from '@cmps/ng-uikit';
import { TranslateService } from '@ngx-translate/core';
import { CalendarEvent } from 'angular-calendar';
import { isWithinInterval } from 'date-fns';
import { interval, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';

import { AuthService } from '../../../auth/auth.service';
import {
    CmpsForm,
    CmpsFormBuilder,
} from '../../../coaching-observation/experimental.form';
import { DateProviders } from '../../../common/component/datetime-picker';
import {
    TakeUntilDestroy,
    takeUntilDestroy,
} from '../../../common/take-until-destroy.decorator';
import { StateToProps } from '../../../store';
import { useDispatcher, UseStore } from '../../../store.decorator';
import { SearchUserContainerComponent } from '../../../user/search-user.container.component';
import { User } from '../../../user/user.model';
import { TranslationKeyMap } from '../../coaching-insight.i18n';
import { CommitmentSummaryItem } from '../commitment-summary-item.model';
import {
    EventFormAction,
    EventParticipant,
    EventType,
    ScheduleEvent,
    ScheduleEventAcceptanceStatus,
    ScheduleEventParticipantRole,
    ScheduleEventStatus,
} from '../models/schedule-event.model';
import { ScheduleItem } from '../models/schedule.model';

import {
    initRetrieveEvent,
    retrieveCommitments,
    retrieveEvents,
    save,
    scheduleEventClear,
    update,
} from './schedule-event.action';
import {
    ScheduleEventFormFields,
    ScheduleEventFormValue,
} from './schedule-event.form';
import scheduleEventReducer from './schedule-event.reducer';
import { RootState } from './schedule-event.state';
import { ScheduleEventSelectEventDialogComponent } from './select-event-dialog.component';

const defaultEventDuration = 30 * 60000;

@Component({
    selector: '[cmpsScheduleEventContainer]',
    templateUrl: './schedule-event.container.component.html',
    providers: [DateProviders],
})
@TakeUntilDestroy()
@UseStore<RootState>(
    'coachingInsight.scheduleEvent',
    scheduleEventReducer.scheduleEvent,
)
export class ScheduleEventContainerComponent
    implements OnInit, TakeUntilDestroy, StateToProps<RootState>
{
    state: RootState;

    @Input() userId: number;
    @Input() requiredPerformer: EventParticipant; // used in new event by one performer (slot calendar launch)
    @Input() startDate: Date; // used in new event by one performer (slot calendar launch)
    @Input() calendarEvents: Array<CalendarEvent<ScheduleItem>> = [];
    @Output() closed: EventEmitter<ScheduleEvent>;

    /** input */ actionOnForm: string = 'new';
    /** input */ eventId: number;
    overlapOtherEvent: boolean = false; // band to check overlap events in update mode

    /** @private */ _eventTypes: IOption[];
    /** @private */ _form: CmpsForm<ScheduleEventFormFields>;
    /** @private */ _eventData: ScheduleEventFormValue;
    /** @private */ _today: Date;

    /** @private */ _i18n = TranslationKeyMap;

    private _minDateSub: Subscription;

    _selectedToAssociate: CommitmentSummaryItem[] = [];
    _selected: number[] = [];
    availableCommitmentsForUser: CommitmentSummaryItem[] = [];
    _submitted = false;

    // TODO: when multiple participants are to be implemented, use different structure
    requiredParticipant: User;

    private _modalService: NgbModal;
    private _searchModal: NgbModalRef;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get date() {
        return this._form.form.controls[this._form.fields.DATE.name].value;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get checkedCommitments() {
        return (
            this._form.form.controls[this._form.fields.COMMITMENTS.name]
                .value || []
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get participants() {
        return this._form.form.controls[this._form.fields.PARTICIPANTS.name]
            .value;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get showAssociateRibbon() {
        return (
            (!this._selectedToAssociate.length &&
                this.checkedCommitments.length) ||
            (this._selectedToAssociate.length &&
                this._selectedToAssociate.length !==
                    this.checkedCommitments.length)
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get isActionNew() {
        return this.actionOnForm === EventFormAction.NEW;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get isActionNewSlot() {
        return this.actionOnForm === EventFormAction.NEWWITHSLOT;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get isActionUpdate() {
        return this.actionOnForm === EventFormAction.UPDATE;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get isActionCancel() {
        return this.actionOnForm === EventFormAction.CANCEL;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get startTimeValue() {
        return this._form.form.controls[this._form.fields.STARTTIME.name].value;
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    get endTimeValue() {
        return this._form.form.controls[this._form.fields.ENDTIME.name].value;
    }

    constructor(
        private readonly _ngZone: NgZone,
        private readonly _formBuilder: CmpsFormBuilder,
        private readonly _injector: Injector,
        private readonly _authService: AuthService,
        private readonly _translateService: TranslateService,
        private readonly _dialog: CmpsDialog,
    ) {
        this.closed = new EventEmitter<ScheduleEvent>(null);
    }

    ngOnInit(): void {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions, @typescript-eslint/no-unnecessary-type-assertion
        this._modalService = <NgbModal>this._injector.get(NgbModal);

        this._form = this._formBuilder.create<ScheduleEventFormFields>(
            ScheduleEventFormFields,
        );

        this._setLocalDefault();

        if (this.actionOnForm === EventFormAction.NEW) {
            this._setFormDefault();
        }

        if (this.actionOnForm === EventFormAction.NEWWITHSLOT) {
            this._setFormNewSlotDefault();
            this._getCommitmetsByPerformer();
        }

        if (
            this.actionOnForm === EventFormAction.UPDATE ||
            this.actionOnForm === EventFormAction.CANCEL
        ) {
            useDispatcher(this, initRetrieveEvent({ eventId: this.eventId }));
        }

        // Check overlap when From Input change
        this._form.form.controls[this._form.fields.STARTTIME.name].valueChanges
            .pipe(
                tap((val) => {
                    if (val instanceof Date) {
                        // check ovelap events
                        if (
                            this.actionOnForm === EventFormAction.UPDATE ||
                            this.actionOnForm === EventFormAction.NEWWITHSLOT ||
                            this.actionOnForm === EventFormAction.NEW
                        ) {
                            this.overlapOtherEvent = this._checkOvelapEvent();
                        }
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        // Check overlap when To Input change
        this._form.form.controls[this._form.fields.ENDTIME.name].valueChanges
            .pipe(
                tap((val) => {
                    if (val instanceof Date) {
                        // check ovelap events
                        if (
                            this.actionOnForm === EventFormAction.UPDATE ||
                            this.actionOnForm === EventFormAction.NEWWITHSLOT ||
                            this.actionOnForm === EventFormAction.NEW
                        ) {
                            this.overlapOtherEvent = this._checkOvelapEvent();
                        }
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._form.form.controls[this._form.fields.DATE.name].valueChanges
            .pipe(
                tap((val) => {
                    if (val instanceof Date) {
                        // update start and end time when date(calendar) change in the form
                        const startDate: Date = new Date(
                            this._form.form.controls[
                                this._form.fields.STARTTIME.name
                            ].value,
                        );
                        const endDate: Date = new Date(
                            this._form.form.controls[
                                this._form.fields.ENDTIME.name
                            ].value,
                        );
                        if (val.getDate() !== startDate.getDate()) {
                            startDate.setFullYear(
                                val.getFullYear(),
                                val.getMonth(),
                                val.getDate(),
                            );
                            endDate.setFullYear(
                                val.getFullYear(),
                                val.getMonth(),
                                val.getDate(),
                            );

                            this._form.form.patchValue(
                                {
                                    [this._form.fields.STARTTIME.name]:
                                        startDate,
                                },
                                { emitEvent: false },
                            );
                            this._form.form.patchValue(
                                { [this._form.fields.ENDTIME.name]: endDate },
                                { emitEvent: false },
                            );
                        }

                        // check ovelap events
                        if (
                            this.actionOnForm === EventFormAction.UPDATE ||
                            this.actionOnForm === EventFormAction.NEWWITHSLOT ||
                            this.actionOnForm === EventFormAction.NEW
                        ) {
                            this.overlapOtherEvent = this._checkOvelapEvent();
                        }

                        // load events in base user required
                        if (this.actionOnForm === EventFormAction.NEW) {
                            const startDate2: Date =
                                this._form.form.controls[
                                    this._form.fields.DATE.name
                                ].value;
                            useDispatcher(
                                this,
                                retrieveEvents({
                                    userId: this.requiredParticipant.id,
                                    startDate: startDate2,
                                }),
                            );
                        }
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._eventTypes = [
            {
                label: this._translateService.instant(
                    TranslationKeyMap._9548_OBSERVATION,
                ),
                value: EventType.OBSERVATION,
            },
            {
                label: this._translateService.instant(
                    TranslationKeyMap._7533_COMMITMENT,
                ),
                value: EventType.COMMITMENT,
            },
        ];
    }

    ngOnDestroy(): void {
        useDispatcher(this, scheduleEventClear({}));
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    stateToProps(prevState?: RootState) {
        if (
            prevState.savedEvent !== this.state.savedEvent &&
            this.state.savedEvent
        ) {
            this.closed.emit(this.state.savedEvent);
        }
        if (prevState.currentCommitments !== this.state.currentCommitments) {
            this.availableCommitmentsForUser = [
                ...this.state.currentCommitments,
            ];

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (
                // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
                this.state.scheduleEventFormValue &&
                this.state.scheduleEventFormValue.commitments
            ) {
                const associatedCommitments =
                    this.state.scheduleEventFormValue.commitments.map((cm) => {
                        return this.availableCommitmentsForUser.find(
                            (c) => c.commitmentId === cm.commitmentId,
                        );
                    });

                this._selectedToAssociate = [...associatedCommitments];

                if (this._form) {
                    this._form.form.patchValue({
                        [this._form.fields.COMMITMENTS.name]:
                            associatedCommitments,
                    });
                }
            }
        }
        if (
            prevState.scheduleEventFormValue &&
            this.state.scheduleEventFormValue &&
            prevState.scheduleEventFormValue.eventId !==
                this.state.scheduleEventFormValue.eventId
        ) {
            this._setFormUpdate();
            this._getCommitmentsUpdate();
        }

        if (prevState.calendarEvents !== this.state.calendarEvents) {
            if (this.actionOnForm === EventFormAction.NEW) {
                this.calendarEvents = this.state.calendarEvents;
                this.overlapOtherEvent = this._checkOvelapEvent();
            }
        }
    }

    propsToDispatch(): void {
        // this._store.dispatch(scheduleEventClear(this._storePath));
    }

    _setLocalDefault(): void {
        this._selectedToAssociate = [];
        this._selected = [];
        this.availableCommitmentsForUser = [];
        this._submitted = false;
    }

    _setFormDefault(): void {
        // default to 9am the next day
        const today = new Date();
        const starttime = new Date();

        starttime.setHours(9);
        starttime.setMinutes(0);
        starttime.setMilliseconds(0);
        if (+today > +starttime) {
            today.getMinutes() >= 30
                ? starttime.setHours(today.getHours() + 1)
                : starttime.setHours(today.getHours(), 30, 0, 0);
        }
        const endtime = new Date(starttime.getTime() + defaultEventDuration);

        this._form.form.patchValue({
            [this._form.fields.STARTTIME.name]: starttime,
            [this._form.fields.ENDTIME.name]: endtime,
            [this._form.fields.DATE.name]: starttime,
            [this._form.fields.PARTICIPANTS.name]: [],
            [this._form.fields.COMMITMENTS.name]: [],
        });
        this._watchDateTimeAndDuration();

        this._appendUserToParticipants(
            this._authService.user.id,
            ScheduleEventParticipantRole.ORGANIZER,
            ScheduleEventAcceptanceStatus.ACCEPTED,
        );
    }

    /**
     * function for updating _minDate/_today relative to current time
     * @function _watchDateTimeAndDuration()
     * @returns void
     */
    private _watchDateTimeAndDuration(): void {
        const check = (): void => {
            this._today = new Date();
            this._today.setMilliseconds(0);
        };
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        this._minDateSub && this._minDateSub.unsubscribe();
        this._minDateSub = interval(60000)
            .pipe(
                tap(() => check()),
                takeUntilDestroy(this),
            )
            .subscribe();
        check();
    }

    _setFormNewSlotDefault(): void {
        const starttime = this.startDate;
        const endtime = new Date(starttime.getTime() + defaultEventDuration);
        this._form.form.controls[this._form.fields.STARTTIME.name].setValue(
            starttime,
        );
        this._form.form.controls[this._form.fields.ENDTIME.name].setValue(
            endtime,
        );
        this._form.form.controls[this._form.fields.DATE.name].setValue(
            starttime,
        );
        this._form.form.controls[this._form.fields.PARTICIPANTS.name].setValue(
            [],
        );
        this._form.form.controls[this._form.fields.COMMITMENTS.name].setValue(
            [],
        );
        this._appendUserToParticipants(
            this._authService.user.id,
            ScheduleEventParticipantRole.ORGANIZER,
            ScheduleEventAcceptanceStatus.ACCEPTED,
        );
        this._appendUserToParticipants(
            this.requiredPerformer.userId,
            ScheduleEventParticipantRole.REQUIRED_PARTICIPANT,
            ScheduleEventAcceptanceStatus.ACCEPTED,
        );
    }

    _setFormUpdate(): void {
        this._form.form.patchValue({
            [this._form.fields.STARTTIME.name]:
                this.state.scheduleEventFormValue.date,
            [this._form.fields.ENDTIME.name]:
                this.state.scheduleEventFormValue.endTime,
            [this._form.fields.DATE.name]:
                this.state.scheduleEventFormValue.date,
            [this._form.fields.EVENTTYPEID.name]:
                this.state.scheduleEventFormValue.eventTypeId,
            [this._form.fields.PARTICIPANTS.name]:
                this.state.scheduleEventFormValue.participants,
            [this._form.fields.COMMITMENTS.name]:
                this.state.scheduleEventFormValue.commitments,
        });
    }

    _appendUserToParticipants(
        userid: number,
        role: ScheduleEventParticipantRole = ScheduleEventParticipantRole.ORGANIZER,
        accept: ScheduleEventAcceptanceStatus = ScheduleEventAcceptanceStatus.ACCEPTED,
    ): void {
        const participant: EventParticipant = {
            eventId: 0,
            userId: userid,
            participantRoleId: role,
            acceptanceStatusId: accept,
            updatedBy: null,
            updatedOn: null,
        };
        const participantlist = this.participants ? [...this.participants] : [];

        // any new participants are non-organizers after first call
        if (
            (participantlist.length &&
                !participantlist.find((a) => a.userId === userid)) ||
            !participantlist.length
        ) {
            participantlist.push(participant);
        }

        this._form.form.controls[this._form.fields.PARTICIPANTS.name].setValue(
            participantlist,
        );
    }

    _onClose(): void {
        this.closed.emit();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _onShowUserSearch(e: MouseEvent) {
        // is not posible change participant in edition
        if (this.isActionNew) {
            this._searchModal = this._modalService.open(
                SearchUserContainerComponent,
                {
                    size: 'lg',
                    centered: true,
                    windowClass: 'schedule-event-search',
                },
            );

            this._searchModal.componentInstance.userId = this
                .requiredParticipant
                ? this.requiredParticipant.id
                : this._authService.user.id;
            this._searchModal.componentInstance.select
                .pipe(
                    tap((user: User) => {
                        this._appendUserToParticipants(
                            user.id,
                            ScheduleEventParticipantRole.REQUIRED_PARTICIPANT,
                            ScheduleEventAcceptanceStatus.NO_RESPONSE,
                        );

                        this.requiredParticipant = user;

                        useDispatcher(
                            this,
                            retrieveCommitments({ userId: user.id }),
                        );

                        // load events in base user required
                        if (this.actionOnForm === EventFormAction.NEW) {
                            const startDate: Date =
                                this._form.form.controls[
                                    this._form.fields.DATE.name
                                ].value;
                            useDispatcher(
                                this,
                                retrieveEvents({
                                    userId: this.requiredParticipant.id,
                                    startDate,
                                }),
                            );
                        }

                        this._searchModal.close();
                    }),
                )
                .subscribe();
        }
    }

    _getParticipantName(user: User): string {
        if (user) {
            return `${user.firstName} ${user.lastName || ''}`;
        } else {
            return null;
        }
    }

    /* Get commitments when form is in update mode */
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _getCommitmentsUpdate() {
        // eslint-disable-next-line array-callback-return
        this.state.scheduleEventFormValue.participants.map((p) => {
            if (
                p.participantRoleId ===
                ScheduleEventParticipantRole.REQUIRED_PARTICIPANT
            ) {
                this.requiredParticipant = new User();
                this.requiredParticipant.firstName = p.firstName;
                this.requiredParticipant.id = p.userId;
                useDispatcher(this, retrieveCommitments({ userId: p.userId }));
            }
        });
    }

    /* Get commitments when form is in new event by performer */
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    _getCommitmetsByPerformer() {
        this.requiredParticipant = new User();
        this.requiredParticipant.firstName = this.requiredPerformer.firstName;
        this.requiredParticipant.id = this.requiredPerformer.userId;
        useDispatcher(
            this,
            retrieveCommitments({ userId: this.requiredPerformer.userId }),
        );
    }

    private _onAssociate(): void {
        this._selectedToAssociate = [...this.checkedCommitments];
        this.availableCommitmentsForUser.forEach((a) => {
            const c = this._selectedToAssociate.find(
                (h) => h.commitmentId === a.commitmentId,
            );
            if (c) {
                a.disabled = true;
            }
        });
        // this._store.dispatch(associateCommitmentsToEvent(this._store, t))
    }

    _onSaveEvent(): void {
        this._submitted = true;
        this._form.checkWarningsErrors();
        if (this._form.form.valid) {
            this.state.scheduleEventFormValue = this._form.form.value;
            // get only associated commitments
            this.state.scheduleEventFormValue.commitments =
                this._selectedToAssociate;
            this.state.scheduleEventFormValue.eventStatusId =
                ScheduleEventStatus.ACTIVE;
            useDispatcher(this, save({}));
        }
    }

    /* Update event action */
    _onUpdateEvent(): void {
        this._form.checkWarningsErrors();
        this.state.scheduleEventFormValue = this._form.form.value;
        this.state.scheduleEventFormValue.eventId = this.eventId;
        // get only associated commitments
        this.state.scheduleEventFormValue.commitments =
            this._selectedToAssociate;
        this.state.scheduleEventFormValue.eventStatusId =
            ScheduleEventStatus.ACTIVE;
        useDispatcher(this, update({}));
        // this.closed.emit();
    }

    /* Cancel event action (same _onUpdateEvent but changing eventStatusId) */
    _onCancelEvent(): void {
        this._form.checkWarningsErrors();
        this.state.scheduleEventFormValue = this._form.form.value;
        this.state.scheduleEventFormValue.eventId = this.eventId;
        // get only associated commitments
        this.state.scheduleEventFormValue.commitments =
            this._selectedToAssociate;
        this.state.scheduleEventFormValue.eventStatusId =
            ScheduleEventStatus.CANCELLED;
        useDispatcher(this, update({}));
        this.closed.emit();
    }

    /** @private */
    _onSelectEventsClick(): void {
        const ref = this._dialog.open(ScheduleEventSelectEventDialogComponent, {
            data: {
                checkedCommitments: [
                    ...(this._form.form.controls[
                        this._form.fields.COMMITMENTS.name
                    ].value || []),
                ],
                availableCommitmentsForUser: this.availableCommitmentsForUser,
            },
        });
        ref.result
            .pipe(
                tap((ret) => {
                    if (!ret) {
                        return;
                    }
                    this._form.form.controls[
                        this._form.fields.COMMITMENTS.name
                    ].setValue(ret);
                    this._onAssociate();
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    /* Check overlap events when is in update mode */
    private _checkOvelapEvent(): boolean {
        // merge DATE and STARTIME in one date
        const inputStartDate: Date = new Date(
            this._form.form.controls[this._form.fields.DATE.name].value,
        );
        const startDate: Date = new Date(
            this._form.form.controls[this._form.fields.STARTTIME.name].value,
        );
        inputStartDate.setHours(startDate.getHours());
        inputStartDate.setMinutes(startDate.getMinutes());
        inputStartDate.setSeconds(0);

        const inputEndDate: Date = new Date(
            this._form.form.controls[this._form.fields.DATE.name].value,
        );
        const endDate: Date = new Date(
            this._form.form.controls[this._form.fields.ENDTIME.name].value,
        );
        inputEndDate.setHours(endDate.getHours());
        inputEndDate.setMinutes(endDate.getMinutes());
        inputEndDate.setSeconds(0);

        // remove event from calendarEvents list when is in edit mode
        if (this.actionOnForm === EventFormAction.UPDATE) {
            for (let i = 0; i < this.calendarEvents.length; i++) {
                if (
                    this.calendarEvents[i].meta.scheduledEventId ===
                    this.eventId
                ) {
                    this.calendarEvents.splice(i, 1);
                }
            }
        }

        // eslint-disable-next-line array-callback-return
        return this.calendarEvents.some((e) => {
            if (
                isWithinInterval(inputStartDate, {
                    start: e.start,
                    end: e.end,
                }) ||
                isWithinInterval(inputEndDate, {
                    start: e.start,
                    end: e.end,
                }) ||
                isWithinInterval(e.start, {
                    start: inputStartDate,
                    end: inputEndDate,
                }) ||
                isWithinInterval(e.end, {
                    start: inputStartDate,
                    end: inputEndDate,
                })
            ) {
                return true;
            }
        });
    }

    // TODO: for use with PopService with nested popups
    // _onShowCurrentCommitments(e: MouseEvent): void {
    //     e.stopPropagation();
    // const target = e.target as HTMLElement;
    // const ref = this._popUpService.show(CurrentCommitmentsComponent, {
    //     target,
    //     placement: 'right-auto'
    // });
    // ref.componentRef.instance.userId = this.userId;
    // ref.componentRef.instance.closed.pipe(
    //     takeUntil(from(ref.result)),
    //     catchError(() => of(null))
    // ).subscribe(e => {
    //     ref.close();
    // })
    // this._commitmentsModal = this._modalService.open(CurrentCommitmentsComponent, {
    //     size: 'sm',
    //     windowClass: 'current-commitments-modal'
    // });
    // }
}
